import React, { useCallback, useMemo } from "react";
import { App, Button, Card, Col, Input, Row, Space, Tag, Tooltip, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../Routes/Routes";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import {
  buildSelectedResultFromAiSalesOffer,
  canOpenAiSalesOfferInPackagesProfile,
} from "../../../utils/aiSalesOfferOpenPackage";
import { buildWhatsAppMeUrl } from "../../../utils/whatsappDeepLink";

function fmtMoney(n, cur) {
  if (n == null || n === "") return null;
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  return cur === "USD" ? `$${Math.round(x).toLocaleString()}` : `₪${Math.round(x).toLocaleString()}`;
}

function parsePayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw;
  return null;
}

function normalizeMessagePayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  const full = raw.full_text != null ? String(raw.full_text) : "";
  const short = raw.short_text != null ? String(raw.short_text) : "";
  const cta = raw.cta_text != null ? String(raw.cta_text) : "";
  if (!full.trim() && !short.trim()) return null;
  return { full_text: full, short_text: short, cta_text: cta };
}

export default function LeadOffers({
  offers = [],
  leadId = null,
  customerPhone = "",
  recommendationRun = null,
  draftAiMessage = null,
  onRefreshMessage,
  refreshMessageLoading = false,
}) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { setSelectedResult_resetAllFields } = useSelectedResult();

  const aiMessage = useMemo(() => {
    const persisted = recommendationRun?.message_payload_json;
    return (
      normalizeMessagePayload(persisted) ||
      normalizeMessagePayload(draftAiMessage) ||
      null
    );
  }, [recommendationRun, draftAiMessage]);

  const displayFullText = useMemo(() => (aiMessage?.full_text || "").trim(), [aiMessage]);

  const textForShare = useMemo(() => {
    if (!aiMessage) return "";
    const core = (aiMessage.full_text || "").trim();
    const cta = (aiMessage.cta_text || "").trim();
    if (!cta) return core;
    if (!core) return cta;
    return `${core}\n\n${cta}`;
  }, [aiMessage]);


  const openPackage = useCallback(
    (offer) => {
      const built = buildSelectedResultFromAiSalesOffer(offer);
      if (!built.ok) {
        message.error("לא ניתן לפתוח את החבילה — חסרים נתונים או מבנה לא תקין.");
        return;
      }
      try {
        setSelectedResult_resetAllFields(built.selectedResult);
        navigate(`/${ROUTES.PACKAGE_PROFILE}`, { state: { fromAiSales: true, leadId } });
      } catch (e) {
        console.error("[LeadOffers] open package failed", e);
        message.error("שגיאה בפתיחת החבילה. נסו שוב.");
      }
    },
    [leadId, message, navigate, setSelectedResult_resetAllFields]
  );

  const copyFullMessage = useCallback(async () => {
    const t = textForShare.trim();
    if (!t) {
      message.warning("אין טקסט להעתקה");
      return;
    }
    try {
      await navigator.clipboard.writeText(t);
      message.success("הועתק ללוח");
    } catch {
      message.error("העתקה נכשלה");
    }
  }, [textForShare, message]);

  const openWhatsApp = useCallback(() => {
    const url = buildWhatsAppMeUrl(customerPhone, textForShare);
    if (!url) {
      message.warning("חסר מספר טלפון תקין או שההודעה ריקה");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, [customerPhone, textForShare, message]);

  if (!offers.length) {
    return <Typography.Text type="secondary">עדיין לא נוצרו הצעות</Typography.Text>;
  }

  return (
    <>
    <Row gutter={[12, 12]}>
      {offers.map((o) => {
        const { ok } = canOpenAiSalesOfferInPackagesProfile(o);
        const payload = parsePayload(o.source_payload_json);
        const aiReason =
          payload?.ai_recommendation?.reason_he ||
          payload?.ai_recommendation?.reasonHe ||
          null;
        return (
          <Col xs={24} md={12} lg={8} key={o.id}>
            <Card size="small" title={o.title} extra={<Typography.Text type="secondary">{o.offer_type}</Typography.Text>}>
              <Typography.Title level={4} style={{ margin: "8px 0" }}>
                {fmtMoney(o.price_ils, "ILS") || "—"}
              </Typography.Title>
              {o.price_usd != null && Number(o.price_usd) > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {fmtMoney(o.price_usd, "USD")}
                </Typography.Text>
              )}
              <div style={{ marginTop: 8 }}>
                <div>{o.hotel_name}</div>
                <Typography.Text type="secondary">
                  {o.destination} · {o.departure_date} → {o.return_date}
                </Typography.Text>
              </div>
              {aiReason ? (
                <Typography.Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0, fontSize: 13 }}>
                  {aiReason}
                </Typography.Paragraph>
              ) : null}
              <Tooltip title={!ok ? "חסר מידע לפתיחת חבילה" : undefined}>
                <Button
                  type="primary"
                  block
                  style={{ marginTop: 12 }}
                  disabled={!ok}
                  onClick={() => openPackage(o)}
                >
                  פתח חבילה
                </Button>
              </Tooltip>
            </Card>
          </Col>
        );
      })}
    </Row>

    <Card
      size="small"
      style={{ marginTop: 20 }}
      title={
        <Space wrap size="small">
          <span>הודעה מוכנה ללקוח</span>
          {recommendationRun?.status ? (
            <Tag>{recommendationRun.status}</Tag>
          ) : null}
        </Space>
      }
    >
      {aiMessage ? (
        <>
          <Input.TextArea
            value={displayFullText}
            readOnly
            autoSize={{ minRows: 6, maxRows: 16 }}
            style={{ fontSize: 14 }}
          />
          <Space wrap style={{ marginTop: 12 }}>
            <Button onClick={copyFullMessage}>העתק</Button>
            <Button type="primary" onClick={openWhatsApp}>
              פתח בוואטסאפ
            </Button>
            {typeof onRefreshMessage === "function" ? (
              <Button loading={refreshMessageLoading} onClick={() => onRefreshMessage()}>
                רענן הודעה
              </Button>
            ) : null}
          </Space>
        </>
      ) : (
        <Typography.Text type="secondary">
          לאחר יצירת הצעות יוצג כאן טקסט מוכן לשליחה ללקוח (נשמר גם בשרת לאחר הרצת מיגרציה).
        </Typography.Text>
      )}
    </Card>
    </>
  );
}
